/**
 * The public Trust Centre (trust.pdpo.ai) — the one place a person can read an
 * organisation's privacy page, its published policies, and use the consent
 * and request links. Every customer-facing page links back to the
 * organisation's page there, so the URL shapes live here and nowhere else.
 *
 * Customer-facing pages offer the actions, not only the notice: the privacy
 * page (/p/<slug>), "request your data" (/dsar/<public_portal_token>) and
 * "report a concern" (/report/<public_portal_token>) — the same forms the
 * Trust Centre page itself links to — plus the signed-in customer portal.
 * `trustLinks()` mirrors public-pages `trustLinks()` so every surface builds
 * the same URLs. Never invent a URL: a link whose token is missing is null.
 */

export const TRUST_CENTRE_URL = 'https://trust.pdpo.ai';

/** The signed-in customer portal — everything held about the person, one tap away. */
export const CUSTOMER_PORTAL_URL = 'https://go.pdpo.ai/portal';

/**
 * The portal's "update my details" deep link. `?intent=details` opens the
 * update sheet straight after sign-in. The same string is built by the CRM
 * and by the Yard, so the query key and value live here and nowhere else.
 */
export const PORTAL_DETAILS_INTENT = 'details';
export function portalDetailsUrl(): string {
  return `${CUSTOMER_PORTAL_URL}?intent=${PORTAL_DETAILS_INTENT}`;
}

/** The organisation's Trust Centre page, from its public slug. */
export function trustCentreUrl(slug: string): string {
  return `${TRUST_CENTRE_URL}/p/${slug}`;
}

/** "Request a copy or correction of your data", addressed through the organisation's public portal token. */
export function trustRequestUrl(portalToken: string): string {
  return `${TRUST_CENTRE_URL}/dsar/${portalToken}`;
}

/** "Report a concern about how your data was handled", addressed through the organisation's public portal token. */
export function trustReportUrl(portalToken: string): string {
  return `${TRUST_CENTRE_URL}/report/${portalToken}`;
}

export interface TrustLinks {
  /** The privacy notice page; null until the organisation claims a public slug. */
  url: string | null;
  /** Request a copy or correction of your data; null without a portal token. */
  requestUrl: string | null;
  /** Report a concern about how your data was handled; null without a portal token. */
  reportUrl: string | null;
  /** The signed-in customer portal. */
  portalUrl: string;
}

/**
 * The links a customer-facing page offers for one organisation, from the
 * slug and public portal token it already holds. Mirrors public-pages
 * `trustLinks(slug, portalToken)`.
 */
export function trustLinks(slug: string | null | undefined, portalToken: string | null | undefined): TrustLinks {
  return {
    url: slug ? trustCentreUrl(slug) : null,
    requestUrl: portalToken ? trustRequestUrl(portalToken) : null,
    reportUrl: portalToken ? trustReportUrl(portalToken) : null,
    portalUrl: CUSTOMER_PORTAL_URL,
  };
}
